/**
 * @Author: Anandp
 * @Date:   2024-04-17 18:40:49
 * @Last Modified by:   Anandp
 * @Last Modified time: 2025-01-13 22:44:23
 */
import { createContext, useCallback, useEffect, useReducer, useState } from "react";
import PropTypes from "prop-types";
import { authApi } from "src/api/auth";
import axios from "axios";
import * as msal from "@azure/msal-browser";
import { Url, siteConstant } from "src/config";
import { loginRequest } from "src/libs/auth-config";
import loginApiService from "src/services/loginVerifyAPI";
import CoachesCornerApiServices from 'src/services/coachesCornerApi';
import { useLocation, useNavigate } from "react-router";
import customerApiService from "src/services/customerApi";
import CryptoJS from 'crypto-js';
import mediaUploadApis from "src/services/mediaUploadApi";

// Silent refresh: refresh the access token 2 minutes before it expires (every 13 min)
// const ACCESS_TOKEN_REFRESH_INTERVAL_MS = 13 * 60 * 1000; // 13 minutes

let pc = null;
let issuer = null;

var ActionType;
(function (ActionType) {
  ActionType["INITIALIZE"] = "INITIALIZE";
  ActionType["SIGN_IN"] = "SIGN_IN";
  ActionType["SIGN_UP"] = "SIGN_UP";
  ActionType["SIGN_OUT"] = "SIGN_OUT";
  ActionType["SET_ERROR_CASE"] = "SET_ERROR_CASE";
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accessToken: null,
  errorCase: false
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, issuer } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      issuer
    };
  },
  SIGN_IN: (state, action) => {
    const { user, isAuthenticated, errorCase } = action.payload;

    return {
      ...state,
      isAuthenticated: isAuthenticated,
      user,
      errorCase: errorCase,
    };
  },
  SIGN_UP: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  SET_ERROR_CASE: (state, action) => {
    return {
      ...state,
      errorCase: action.payload,
    };
  },
  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  initialize: () => Promise.resolve(),
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve(),
  getAccessTocken: () => Promise.resolve(),
  setErrorCase: () => { },
});

export const AuthProvider = (props) => {
  const { children } = props;
  issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
  // eslint-disable-next-line
  const [publicClient, setPublicClient] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);
  const searchParams = new URLSearchParams(window.location.search);
  const islLoginParam = searchParams.get('isllogin');
  const userLoginParam = searchParams.get('userlogin');
  // eslint-disable-next-line
  const tokenParameter = searchParams.get('token');
  const islAdminLoginParameter = searchParams.get('isladminlogin');
    const location = useLocation();
    const publicRoutes = ['/','/login', '/forgotPassword','/verifyOtp','/changePassword','/myMojoSurvey','/mtpsurvey','/workWithSurvey','/wiredUpSurvey','/MyEmotionalResilienceSurvey','/leadershipBrandSurvey','/myLeadershipSurvey','/teamEffectivenessSurvey','/redirectingphase','/surveyQuestions','/termsofservice'];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  const navigate = useNavigate();
  // Ref to hold the silent refresh interval so we can clear it on logout
  // const silentRefreshTimerRef = useRef(null);

  const setErrorCase = (value) => {
    dispatch({ type: ActionType.SET_ERROR_CASE, payload: value });
  };

  const decryptData = (encryptedData, key) => {
    if (!encryptedData || !key) {
      console.warn("Missing encrypted data or key", { encryptedData, key });
      return null;
    }
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, key);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      
      // If decryption fails (e.g. wrong key), it might return empty or invalid string
      if (!decrypted) {
        console.warn("Decryption returned empty string - likely wrong key");
        return null;
      }

      try {
        return JSON.parse(decrypted);
      } catch (parseError) {
        console.error("JSON parse failed after decryption:", parseError);
        return null;
      }
    } catch (error) {
      console.error("Decryption failed:", error, { key });
      return null;
    }
  };

  // const stopSilentRefresh = useCallback(() => {
  //   if (silentRefreshTimerRef.current) {
  //     clearInterval(silentRefreshTimerRef.current);
  //     silentRefreshTimerRef.current = null;
  //     console.log('Silent refresh timer stopped');
  //   }
  // }, []);

  // const startSilentRefresh = useCallback(() => {
  //   // Only run for JWT issuer
  //   if (issuer !== 'jwt') return;

  //   // Clear any existing timer first
  //   stopSilentRefresh();

  //   console.log(`Starting silent refresh timer (interval: ${ACCESS_TOKEN_REFRESH_INTERVAL_MS}ms)`);
  //   silentRefreshTimerRef.current = setInterval(async () => {
  //     try {
  //       console.log('Attempting silent token refresh...');
  //       await axios.post(`${Url}/auth/refresh`, {}, { withCredentials: true });
  //       console.log('Silent token refresh successful');
  //     } catch (error) {
  //       console.error('Silent token refresh failed, session expired. Logging out.');
  //       // If refresh fails (e.g., refresh token is expired or blacklisted), sign out
  //       signOut();
  //     }
  //   }, ACCESS_TOKEN_REFRESH_INTERVAL_MS);
  //   //eslint-disable-next-line
  // }, [stopSilentRefresh]);

  // Global axios interceptor: redirect to login on 401 Authentication failed
  useEffect(() => {
    if (!isPublicRoute) {
      console.log("Setting up global axios interceptor for 401 responses");
      const interceptorId = axios.interceptors.response.use(
        (response) => response,
        (error) => {
          const url = error.config?.url || '';
          const data = error.response?.data;
          if (
            !url.includes('/auth/decodeToken') &&
            data?.code === 401 &&
            data?.message === 'Authentication failed'
          ) {
            console.log('Authentication failed, checking for login UUID');
            const loginUUID = sessionStorage.getItem("login_uuid");
            if (!loginUUID) {
              clearUserSession();
              navigate('/', { replace: true });
            } else {
              console.log("Login UUID is present, bypassing 401 redirect.");
            }
          }
          return Promise.reject(error);
        }
      );
      return () => axios.interceptors.response.eject(interceptorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicRoute]);


  // Initialize MSAL client on component mount
  useEffect(() => {
    const initMsal = async () => {
      const currentIssuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
      if (currentIssuer === 'azure' && !pc) {
        console.log("Pre-initializing MSAL client");
        const msConfig = JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
        if (!msConfig.auth?.clientId || !msConfig.auth?.redirectUri) {
          console.error("Invalid MSAL configuration: missing clientId or redirectUri");
          return;
        }
        try {
          pc = new msal.PublicClientApplication(msConfig);
          await pc.initialize();
          setPublicClient(pc);
          console.log("MSAL client pre-initialized successfully");
        } catch (error) {
          console.error("MSAL pre-initialization failed:", error);
          pc = null;
        }
      }
    };

    initMsal();
  }, []);

  const initialize = useCallback(async () => {
    issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
    console.log("initialize called with issuer:", issuer);

    // Helper: attempt silent refresh before giving up
    // const trySilentRefresh = async () => {
    //   try {
    //     await axios.post(`${Url}/auth/refresh`, {}, { withCredentials: true });
    //     return true;
    //   } catch {
    //     return false;
    //   }
    // };

    try {
      if (issuer === 'jwt') {
        let user = null;
        const loginUUID = sessionStorage.getItem("login_uuid");
        try {
          const response = await axios.post(`${Url}/auth/decodeToken`, {}, {
            withCredentials: true
          });
          console.log("response decode", response);
          
          user = response.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");

          // SOFT LOGOUT CHECK: Compare decoded user ID with tab-specific session storage
          const storedUserStr = sessionStorage.getItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
          if (storedUserStr) {
            const storedUser = JSON.parse(storedUserStr);
            // If we have a stored user and the current token returns a different user, trigger soft logout
            if (storedUser && storedUser.userId && user.userId !== storedUser.userId) {
              console.warn("Session conflict detected: User ID mismatch between cookie and tab. Redirecting to login.");
              
              if (!loginUUID) {
                // Clear state and sessionStorage but DO NOT call backend logout (preserve cookie for the other tab)
                await clearUserSession();
                navigate('/', { replace: true });
                return { isAuthenticated: false, user: null };
              } else {
                console.log("Login UUID is present, bypassing soft logout redirect.");
              }
            }
          }
        } catch (tokenError) {
          console.log('Access token decode failed, redirecting to login:', tokenError.message);
          
          if (!loginUUID) {
            dispatch({
              type: ActionType.INITIALIZE,
              payload: { isAuthenticated: false, user: null },
            });
            // Redirect to login page on 401
            if (tokenError.response?.status === 401 || tokenError.message) {
              navigate('/', { replace: true });
            }
            return { isAuthenticated: false, user: null };
          } else {
            console.log("Login UUID is present, bypassing 401 redirect.");
            const storedUserStr = sessionStorage.getItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
            if (storedUserStr) {
               user = JSON.parse(storedUserStr);
               dispatch({
                 type: ActionType.INITIALIZE,
                 payload: { isAuthenticated: true, user: user },
               });
               return { isAuthenticated: true, user }; 
            } else {
              dispatch({
                type: ActionType.INITIALIZE,
                payload: { isAuthenticated: false, user: null },
              });
              navigate('/', { replace: true });
              return { isAuthenticated: false, user: null };
            }
          }
        }
        // user is now set (directly or after silent refresh); store session data
        try {
          // Store user details
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          sessionStorage.setItem("userDetails", JSON.stringify(user));

          // Copy encrypted data from localStorage to sessionStorage if available
          const encryptedJoyRide = localStorage.getItem("joyrideStatus");
          const encryptedDashboardPerms = localStorage.getItem("dashboardPerms");
          const encryptedSurveyPerms = localStorage.getItem("surveyPerms");
          const profilePicture = localStorage.getItem("profilePicture");

          if (encryptedJoyRide) sessionStorage.setItem("joyrideStatus", encryptedJoyRide);
          if (encryptedDashboardPerms) sessionStorage.setItem("dashboardPerms", encryptedDashboardPerms);
          if (encryptedSurveyPerms) sessionStorage.setItem("surveyPerms", encryptedSurveyPerms);
          if (profilePicture) sessionStorage.setItem("profilePicture", profilePicture);

          // Validate encrypted data
          if (encryptedJoyRide && !decryptData(encryptedJoyRide, user.userId.toString())) {
            console.warn("Invalid joyrideStatus, clearing");
            localStorage.removeItem("joyrideStatus");
            sessionStorage.removeItem("joyrideStatus");
          }
          if (encryptedDashboardPerms && !decryptData(encryptedDashboardPerms, user.userId.toString())) {
            console.warn("Invalid dashboardPerms, clearing");
            localStorage.removeItem("dashboardPerms");
            sessionStorage.removeItem("dashboardPerms");
          }
          if (encryptedSurveyPerms && !decryptData(encryptedSurveyPerms, user.userId.toString())) {
            console.warn("Invalid surveyPerms, clearing");
            localStorage.removeItem("surveyPerms");
            sessionStorage.removeItem("surveyPerms");
          }
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          // Start silent refresh now that user is authenticated
          // startSilentRefresh();
          return { isAuthenticated: true, user };
        } catch (error) {
          console.log('No valid session found', error);
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          throw error; // 🔥 error;
        }

      } else if (issuer === 'azure') {
        // Check if we have stored user data from previous session
        const storedUser = sessionStorage.getItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);

        if (storedUser) {
          console.log("Found stored Azure session, restoring user");
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user: JSON.parse(storedUser),
            },
          });
          return;
        }

        if (!pc) {
          console.log("MSAL client not ready, initializing...");
          const msConfig = JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
          if (msConfig.auth?.clientId && msConfig.auth?.redirectUri) {
            pc = new msal.PublicClientApplication(msConfig);
            await pc.initialize();
            setPublicClient(pc);
          } else {
            dispatch({
              type: ActionType.INITIALIZE,
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }
        }

        const redirectResponse = await pc.handleRedirectPromise();
        if (redirectResponse) {
          console.log("Redirect response received:", redirectResponse);
          pc.setActiveAccount(redirectResponse.account);
          const user = await getProfielData(redirectResponse.account);
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          navigate('/dashboard', { replace: true });
          return;
        }

        const accounts = pc.getAllAccounts();
        if (accounts.length > 0) {
          pc.setActiveAccount(accounts[0]);
          const user = await getProfielData(accounts[0]);
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else if (issuer === 'google') {
        // Google auth now uses secure cookies
        try {
          const response = await axios.post(`${Url}/auth/decodeToken`, {}, {
            withCredentials: true
          });
          const user = response.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          navigate('/dashboard', { replace: true });
        } catch (error) {
          dispatch({
            type: ActionType.INITIALIZE,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (err) {
      console.error("Initialize error:", err);
      localStorage.removeItem("joyrideStatus");
      localStorage.removeItem("dashboardPerms");
      localStorage.removeItem("surveyPerms");
      localStorage.removeItem('accessToken');
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      // Redirect to login instead of rethrowing error
      navigate('/', { replace: true });
      return { isAuthenticated: false, user: null };
    }
    // eslint-disable-next-line
  }, [dispatch]);

  // Listen to cross-tab session updates
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "session_updated") {
        console.log("Session updated in another tab. Re-validating...");
        if (!isPublicRoute) {
          initialize();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [isPublicRoute, initialize]);

  // Validate session on route change (sub-menu click)
  useEffect(() => {
    if (!isPublicRoute && state.isAuthenticated) {
      initialize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const signIn = useCallback(
    async (data, method, msConfig) => {
      let user = {};
      issuer = localStorage.getItem(siteConstant.AUTH_ISSUER_STORAGE_KEY);
      console.log("signIn called with:", { issuer, data, method });
      try {
        if (issuer === 'jwt') {
          dispatch({
            type: ActionType.SIGN_IN,
            payload: {
              isAuthenticated: false,
              user: null,
              errorCase: null,
            },
          });
          const response = await axios.post(`${Url}/auth/login`, data, {
            withCredentials: true
          });
          console.log("--response--", response);
          const userProfile = response.data;
          console.log("==userProfile==", userProfile);
          if ((userProfile.changePassword === 1 || userProfile.changePassword === 2) && userProfile.key) {
            localStorage.setItem("resetKey", userProfile.key);
          }
          const token = response.headers["x-access-token"];
          if (!token) throw new Error("No access token received");
          const tokenData = { accessToken: token };
          const responseUser = await axios.post(`${Url}/auth/decodeToken`, tokenData, {
            withCredentials: true
          });
          user = responseUser.data;
          if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
          const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
            customerApiService.getJoyride(token, user.userId).catch(err => {
              console.error("getJoyride error:", err);
              return { data: null };
            }),
            loginApiService.getDashboardIds(token, user.userId).catch(err => {
              console.error("getDashboardIds error:", err);
              return { dashIds: null };
            }),
            loginApiService.getSurveyIds(token, user.userId).catch(err => {
              console.error("getSurveyIds error:", err);
              return { surveyIds: null };
            }),
            mediaUploadApis.getProfilePicture(token, user.userId).catch(err => {
              console.error("getProfilePicture error:", err);
              return null;
            }),
          ]);
          console.log("surveyPerms:", surveyPerms);
          surveyPerms.surveyIds = surveyPerms.surveyIds || surveyPerms.SurveyIds;
          console.log("API Responses:", { joyridestatus, dashboardPerms, surveyPerms, profilePicture });
          const encryptedJoyRide = joyridestatus.data
            ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), user.userId.toString()).toString()
            : null;
          const encryptedDashboardPerms = dashboardPerms.dashIds
            ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), user.userId.toString()).toString()
            : null;
          console.log('surveyperms.surveyIds', surveyPerms.surveyIds);
          const encryptedSurveyPerms = surveyPerms.surveyIds
            ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), user.userId.toString()).toString()
            : null;
          // Token now stored in secure HttpOnly cookie
          sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
          if (data.isChecked) {
            localStorage.setItem("remberMe", "true");
            localStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
            if (encryptedJoyRide) localStorage.setItem("joyrideStatus", encryptedJoyRide);
            if (encryptedDashboardPerms) localStorage.setItem("dashboardPerms", encryptedDashboardPerms);
            if (encryptedSurveyPerms) {
              console.log('inside encrypted survey perms');
              try {
                localStorage.setItem("surveyPerms", encryptedSurveyPerms);
              } catch (error) {
                console.error("Error storing encrypted surveyPerms:", error);
              }
            }
            localStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);
          } else {
            localStorage.removeItem("remberMe"); 
          }
          dispatch({
            type: ActionType.SIGN_IN,
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          
          // Trigger storage event for other tabs
          localStorage.setItem("session_updated", Date.now().toString());
          
          // Start the silent refresh timer upon successful sign in
          // startSilentRefresh();
          // navigating to the dashboard after login
          navigate('/dashboard', { replace: true });
        } else if (issuer === 'azure') {
          console.log("Starting Azure login with popup");
          if (!pc) {
            console.error("PublicClient not initialized, initializing now");
            let msConfigParsed = msConfig || JSON.parse(localStorage.getItem(siteConstant.AZURE_CONFIG_STORAGE_KEY) || '{}');
            if (!msConfigParsed.auth?.clientId || !msConfigParsed.auth?.redirectUri) {
              throw new Error("Invalid MSAL configuration: missing clientId or redirectUri");
            }
            pc = new msal.PublicClientApplication(msConfigParsed);
            await pc.initialize();
            setPublicClient(pc);
          }
          const signInType = method || "loginPopup";
          console.log("signInType:", signInType);
          if (signInType === "loginPopup") {
            try {
              const loginResponse = await pc.loginPopup(loginRequest);
              console.log("Login popup response:", loginResponse);
              pc.setActiveAccount(loginResponse.account);
              user = await getProfielData(loginResponse.account);
              dispatch({
                type: ActionType.SIGN_IN,
                payload: {
                  isAuthenticated: true,
                  user,
                  issuer,
                },
              });
              navigate('/dashboard', { replace: true });
            } catch (error) {
              console.error("Popup error:", error);
              if (error.message.includes("Cross-Origin-Opener-Policy")) {
                console.warn("COOP error detected, polling for accounts");
                let attempts = 0;
                const maxAttempts = 5;
                const pollInterval = 1000;
                const pollAccounts = async () => {
                  while (attempts < maxAttempts) {
                    const accounts = pc.getAllAccounts();
                    if (accounts.length > 0) {
                      pc.setActiveAccount(accounts[0]);
                      user = await getProfielData(accounts[0]);
                      dispatch({
                        type: ActionType.SIGN_IN,
                        payload: {
                          isAuthenticated: true,
                          user,
                          issuer,
                        },
                      });
                      navigate('/dashboard', { replace: true });
                      return true;
                    }
                    attempts++;
                    await new Promise(resolve => setTimeout(resolve, pollInterval));
                  }
                  throw new Error("Authentication failed: No accounts found after polling");
                };
                if (await pollAccounts()) return;
              }
              throw error;
            }
          } else if (signInType === "loginRedirect") {
            await pc.loginRedirect(loginRequest);
          }
        } else if (issuer === 'google') {
          console.log("Starting Google login");
          const googleConfig = JSON.parse(localStorage.getItem(siteConstant.GOOGLE_CONFIG_STORAGE_KEY) || '{}');
          if (!googleConfig.clientId) {
            throw new Error("Invalid Google configuration: missing clientId");
          }

          const loadGoogleScript = () => {
            return new Promise((resolve, reject) => {
              if (window.google?.accounts?.oauth2) {
                resolve();
                return;
              }

              const script = document.createElement('script');
              script.src = 'https://accounts.google.com/gsi/client';
              script.async = true;
              script.defer = true;
              script.onload = () => {
                const checkGoogle = setInterval(() => {
                  if (window.google?.accounts?.oauth2) {
                    clearInterval(checkGoogle);
                    resolve();
                  }
                }, 100);
                setTimeout(() => {
                  clearInterval(checkGoogle);
                  reject(new Error('Google Identity Services timeout'));
                }, 10000);
              };
              script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
              document.head.appendChild(script);
            });
          };

          await loadGoogleScript();

          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: googleConfig.clientId,
            scope: 'email profile',
            login_hint: data.userEmail,
            callback: async (tokenResponse) => {
              try {
                const response = await fetch(`https://www.googleapis.com/oauth2/v1/userinfo?access_token=${tokenResponse.access_token}`);
                const userInfo = await response.json();

                // Validate that authenticated email matches entered email
                if (userInfo.email.toLowerCase() !== data.userEmail.toLowerCase()) {
                  throw new Error(`Please sign in with ${data.userEmail}. You signed in with ${userInfo.email}`);
                }

                const googleAccount = {
                  username: userInfo.email,
                  name: userInfo.name,
                  email: userInfo.email
                };

                user = await getProfielData(googleAccount);
                dispatch({
                  type: ActionType.SIGN_IN,
                  payload: {
                    isAuthenticated: true,
                    user,
                    issuer,
                  },
                });
                navigate('/dashboard', { replace: true });
              } catch (error) {
                console.error("Google OAuth callback error:", error);
                throw error;
              }
            }
          });

          tokenClient.requestAccessToken();
        }
      } catch (error) {
        // 1. Extract the specific data from the response
        const responseData = error.response?.data;

        // 2. Determine the message: Use "Invalid credentials" if code is 401, 
        // otherwise fallback to the server message or generic error.
        let errorMessage = responseData?.message || error.message;

        if (responseData?.code === 401) {
          errorMessage = "Invalid credentials";
          // Optional: Trigger your toast library here
          // toast.error(errorMessage); 
        }

        console.error("signIn error:", error, { issuer, data });

        dispatch({
          type: ActionType.SIGN_IN,
          payload: {
            isAuthenticated: false,
            user: null,
            errorCase: errorMessage, // This will now be "Invalid credentials"
          },
        });
      }
    },
    // eslint-disable-next-line
    [dispatch]
  );

  const signUp = useCallback(
    async (email, name, password) => {
      const { accessToken } = await authApi.signUp({ email, name, password });
      const user = await authApi.me({ accessToken });
      dispatch({
        type: ActionType.SIGN_UP,
        payload: {
          user,
        },
      });
    },
    [dispatch]
  );

  const clearUserSession = useCallback(async () => {
    // stopSilentRefresh();
    sessionStorage.removeItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
    sessionStorage.removeItem("dashboardPerms");
    sessionStorage.removeItem("surveyPerms");
    sessionStorage.removeItem("userDetails");
    sessionStorage.removeItem("teamDashboardTeamId");
    sessionStorage.removeItem("teamDashboardTeamName");
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith("mtpStatus_")) sessionStorage.removeItem(key);
    });
    sessionStorage.removeItem("teamPerms");
    sessionStorage.removeItem("invalidCredentialCount");
    sessionStorage.removeItem("joyrideStatus");
    sessionStorage.removeItem("profilePicture");
    sessionStorage.removeItem("strapiKey");
    sessionStorage.removeItem("login_uuid");

    localStorage.removeItem(siteConstant.AUTH_PROFILE_STORAGE_KEY);
    localStorage.removeItem("dashboardPerms");
    localStorage.removeItem("surveyPerms");
    localStorage.removeItem("joyrideStatus");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("remberMe");
    localStorage.removeItem("profilePicture");

    // Clear provider-specific caches
    if (pc) {
      await pc.clearCache();
    }

    dispatch({
      type: ActionType.SIGN_OUT,
      payload: {
        isAuthenticated: false,
        isInitialized: false,
        user: null,
      },
    });
    //eslint-disable-next-line
  }, [dispatch]);

  const signOut = useCallback(async () => {
    try {
      await axios.post(`${Url}/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.log('Logout error:', error);
    }

    await clearUserSession();
  }, [clearUserSession]);

  const getProfielData = async (account) => {
    let user = {};
    try {
      console.log("getProfielData called with account:", account);
      const data = { userEmail: account.username };
      const response = await axios.post(`${Url}/auth/getProfile`, data);
      const token = response.headers["x-access-token"];
      if (!token) throw new Error("No access token received");
      const tokenData = { accessToken: token };
      const responseUser = await axios.post(`${Url}/auth/decodeToken`, tokenData);
      user = responseUser.data;
      if (user.userId === undefined || user.userId === null) throw new Error("Invalid user ID in token");
      const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
        customerApiService.getJoyride(token, user.userId).catch(err => {
          console.error("getJoyride error:", err);
          return { data: null };
        }),
        loginApiService.getDashboardIds(token, user.userId).catch(err => {
          console.error("getDashboardIds error:", err);
          return { dashIds: null };
        }),
        loginApiService.getSurveyIds(token, user.userId).catch(err => {
          console.error("getSurveyIds error:", err);
          return { surveyIds: null };
        }),
        mediaUploadApis.getProfilePicture(token, user.userId).catch(err => {
          console.error("getProfilePicture error:", err);
          return null;
        }),
      ]);
      const encryptedJoyRide = joyridestatus.data
        ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), user.userId.toString()).toString()
        : null;
      const encryptedDashboardPerms = dashboardPerms.dashIds
        ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), user.userId.toString()).toString()
        : null;
      const encryptedSurveyPerms = surveyPerms.surveyIds
        ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), user.userId.toString()).toString()
        : null;
      sessionStorage.setItem(siteConstant.AUTH_PROFILE_STORAGE_KEY, JSON.stringify(user));
      const rememberMe = localStorage.getItem("remberMe") === "true";
      if (rememberMe) {
        if (encryptedJoyRide) localStorage.setItem("joyrideStatus", encryptedJoyRide);
        if (encryptedDashboardPerms) localStorage.setItem("dashboardPerms", encryptedDashboardPerms);
        if (encryptedSurveyPerms) localStorage.setItem("surveyPerms", encryptedSurveyPerms);
        localStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);
      }
    } catch (error) {
      console.error("getProfielData error:", error);
    }
    return user;
  };

  const getAccessTocken = useCallback(async () => {
    if (!pc) {
      throw Error("MSAL client not initialized");
    }
    const account = pc.getActiveAccount();
    if (!account) {
      throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
    }
    let user = await getProfielData(account);
    dispatch({
      type: ActionType.SIGN_IN,
      payload: {
        isAuthenticated: true,
        isInitialized: true,
        user,
      },
    });
  }, [dispatch]);

  const islLogin = async () => {
    try {
      const decodedData = JSON.parse(
        atob(islLoginParam)
      );
      console.log("decoded from context",decodedData);
      const {
        tenantId,
        userId,
        uuid
      } = decodedData;
      const body = {
        "tenantId": tenantId,
        "userid": userId,
        "uuid": uuid
      };
      const details = await CoachesCornerApiServices.gettoken(body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem("login_uuid", uuid);

        // const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
        //   customerApiService.getJoyride(details.token, afterISLLOGIN),
        //   loginApiService.getDashboardIds(details.token, afterISLLOGIN),
        //   loginApiService.getSurveyIds(details.token, afterISLLOGIN),
        //   mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN)
        // ]);

        // const encryptedJoyRide = joyridestatus.data
        //   ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedDashboardPerms = dashboardPerms.dashIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedSurveyPerms = surveyPerms.surveyIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), afterISLLOGIN.toString()).toString()
        //   : null;

        // if (encryptedJoyRide) sessionStorage.setItem("joyrideStatus", encryptedJoyRide);
        // if (encryptedDashboardPerms) sessionStorage.setItem("dashboardPerms", encryptedDashboardPerms);
        // if (encryptedSurveyPerms) sessionStorage.setItem("surveyPerms", encryptedSurveyPerms);
        // sessionStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);

        await customerApiService.getJoyride(details.token, userId);
        await loginApiService.getDashboardIds(details.token, userId);
        await loginApiService.getSurveyIds(details.token, userId);
        await mediaUploadApis.getProfilePicture(details.token, userId);

        // Trigger storage event for other tabs
        localStorage.setItem("session_updated", Date.now().toString());

        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  const islAdminLogin = async () => {
    try {
      const decodedData = JSON.parse(
        atob(islAdminLoginParameter)
      );

      console.log("decoded from context",decodedData);
      const {
        tenantId,
        userId,
        uuid
      } = decodedData;
      const body = {
        "tenantId": tenantId,
        "userid": userId,
        "uuid": uuid
      };
      const details = await CoachesCornerApiServices.getAdmintoken(body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem("login_uuid", uuid);

        // const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
        //   customerApiService.getJoyride(details.token, afterISLLOGIN),
        //   loginApiService.getDashboardIds(details.token, afterISLLOGIN),
        //   loginApiService.getSurveyIds(details.token, afterISLLOGIN),
        //   mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN)
        // ]);

        // const encryptedJoyRide = joyridestatus.data
        //   ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedDashboardPerms = dashboardPerms.dashIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedSurveyPerms = surveyPerms.surveyIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), afterISLLOGIN.toString()).toString()
        //   : null;

        // if (encryptedJoyRide) sessionStorage.setItem("joyrideStatus", encryptedJoyRide);
        // if (encryptedDashboardPerms) sessionStorage.setItem("dashboardPerms", encryptedDashboardPerms);
        // if (encryptedSurveyPerms) sessionStorage.setItem("surveyPerms", encryptedSurveyPerms);
        // sessionStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);

        await customerApiService.getJoyride(details.token, userId);
        await loginApiService.getDashboardIds(details.token, userId);
        await loginApiService.getSurveyIds(details.token, userId);
        await mediaUploadApis.getProfilePicture(details.token, userId);

        // Trigger storage event for other tabs
        localStorage.setItem("session_updated", Date.now().toString());

        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  const userLogin = async () => {
    try {
      console.log('--inside user details--');
      const decodedData = JSON.parse(
        atob(userLoginParam)
      );

      console.log("decoded from context",decodedData);
      const {
        tenantId,
        userId,
        uuid
      } = decodedData;
      const body = {
        "tenantId": tenantId,
        "userid": userId,
        "uuid": uuid
      };
      const details = await CoachesCornerApiServices.gettoken(body);
      if (details) {
        localStorage.setItem('issuer', 'jwt');
        const stringifiedUser = JSON.stringify(details.data);
        sessionStorage.setItem("userDetails", stringifiedUser);
        sessionStorage.setItem("login_uuid", uuid);

        // const [joyridestatus, dashboardPerms, surveyPerms, profilePicture] = await Promise.all([
        //   customerApiService.getJoyride(details.token, afterISLLOGIN),
        //   loginApiService.getDashboardIds(details.token, afterISLLOGIN),
        //   loginApiService.getSurveyIds(details.token, afterISLLOGIN),
        //   mediaUploadApis.getProfilePicture(details.token, afterISLLOGIN)
        // ]);

        // const encryptedJoyRide = joyridestatus.data
        //   ? CryptoJS.AES.encrypt(JSON.stringify(joyridestatus.data), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedDashboardPerms = dashboardPerms.dashIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(dashboardPerms.dashIds), afterISLLOGIN.toString()).toString()
        //   : null;
        // const encryptedSurveyPerms = surveyPerms.surveyIds
        //   ? CryptoJS.AES.encrypt(JSON.stringify(surveyPerms.surveyIds), afterISLLOGIN.toString()).toString()
        //   : null;

        // if (encryptedJoyRide) sessionStorage.setItem("joyrideStatus", encryptedJoyRide);
        // if (encryptedDashboardPerms) sessionStorage.setItem("dashboardPerms", encryptedDashboardPerms);
        // if (encryptedSurveyPerms) sessionStorage.setItem("surveyPerms", encryptedSurveyPerms);
        // sessionStorage.setItem("profilePicture", profilePicture ? JSON.stringify(profilePicture) : null);

        await customerApiService.getJoyride(details.token, userId);
        await loginApiService.getDashboardIds(details.token, userId);
        await loginApiService.getSurveyIds(details.token, userId);
        await mediaUploadApis.getProfilePicture(details.token, userId);

        // Trigger storage event for other tabs
        localStorage.setItem("session_updated", Date.now().toString());

        await initialize();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error("Error fetching sector data:", error);
    }
  };

  // useEffect(() => {
  //   const init = async () => {
  //     await initialize();
  //   };
  //   init();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);
  useEffect(() => {
    if (isPublicRoute) {
      // Skip auth initialization for public routes
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
      return;
    }

    const init = async () => {
      await initialize();
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicRoute]);
  useEffect(() => {
    if (islLoginParam !== null) {
      islLogin();
    }
    // eslint-disable-next-line
  }, [islLoginParam]);

  useEffect(() => {
    if (userLoginParam !== null) {
      userLogin();
    }
    // eslint-disable-next-line
  }, [userLoginParam]);

  useEffect(() => {
    if (islAdminLoginParameter !== null) {
      islAdminLogin();
    }
    // eslint-disable-next-line
  }, [islAdminLoginParameter]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: issuer,
        signIn,
        signUp,
        signOut,
        initialize,
        getAccessTocken,
        clearUserSession,
        setErrorCase
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const AuthConsumer = AuthContext.Consumer;